/**
 * Tiny fetch core shared by every resource module (ports shop/ui/src/api/http.ts).
 * Responsibilities, and ONLY these, live here:
 *   - prefix the configured base URL (one place for base-url handling);
 *   - inject the Bearer token for authenticated calls;
 *   - JSON-encode request bodies and parse JSON responses;
 *   - turn any non-2xx response (or transport failure) into a typed ApiError.
 *
 * Resource modules (auth, shops, ...) are thin typed wrappers over `request`.
 * The REFRESH itself (and its single-flight de-dupe) is NOT this file's job --
 * that lives in AuthContext, which owns the token lifecycle. This client only
 * decides WHEN to consult it via the injected `onUnauthorized` hook: before a
 * token-less authenticated send (queue behind the boot refresh instead of
 * 401ing) and once after a 401 (retry with the rotated token).
 */
import { API_BASE } from '../config.ts';
import { ApiError, apiErrorFromBody } from './errors.ts';

/**
 * Default request timeout (E2.1 spec section 10): phones on flaky mobile
 * data must surface the error/retry state rather than hang for minutes. A
 * caller-supplied `signal` (the stale-response guards) is combined with this
 * timeout via AbortSignal.any, never replaced by it.
 */
const DEFAULT_TIMEOUT_MS = 20_000;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface RequestOptions {
  /** JSON body; omitted for GET/DELETE. */
  body?: unknown;
  /** Attach the Bearer token (default true). Login sets this false. */
  auth?: boolean;
  /** Query params; undefined values are dropped. */
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/** Supplies the current token (or null). Set by the auth store at construction. */
export type TokenProvider = () => string | null;

export interface HttpClientOptions {
  baseUrl?: string;
  getToken?: TokenProvider;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /**
   * Supplies a token when the client has none or the server rejected one.
   * Called (a) BEFORE sending an authenticated request that has no token yet
   * -- a reload's optimistic render racing the boot refresh -- and (b) on a
   * 401 from an authenticated request. Returns the token to send/retry with,
   * or null to give up (AuthContext wires this to its single-flight refresh,
   * so concurrent callers queue behind ONE refresh). Absent => no gate/retry.
   */
  onUnauthorized?: () => Promise<string | null>;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthorized?: () => Promise<string | null>;

  constructor(opts: HttpClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? API_BASE).replace(/\/$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = opts.onUnauthorized;
  }

  async request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await this.sendWithRetry(method, path, opts);
    return this.parse<T>(res);
  }

  /**
   * Like `request`, but for a binary/text download (e.g. a CSV export). Returns
   * the raw body as a Blob plus the server's suggested filename parsed from the
   * `Content-Disposition` header, and still turns a non-2xx envelope into a typed
   * ApiError so callers branch on the same error grammar as JSON calls.
   */
  async requestBlob(
    method: HttpMethod,
    path: string,
    opts: RequestOptions = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const res = await this.sendWithRetry(method, path, opts);
    if (!res.ok) {
      const text = await res.text();
      throw apiErrorFromBody(res.status, text.length > 0 ? safeJson(text) : undefined);
    }
    const blob = await res.blob();
    return { blob, filename: filenameFromDisposition(res.headers.get('content-disposition')) };
  }

  /**
   * Wraps `send` with two auth rules, both riding `onUnauthorized`:
   *   1. PRE-SEND GATE: an authenticated request with NO token yet (a reload's
   *      optimistic render racing the boot refresh) awaits `onUnauthorized`
   *      BEFORE sending instead of firing a doomed 401 round-trip; concurrent
   *      callers queue behind AuthContext's single-flight refresh. A null
   *      outcome means there is no session -- the request is dropped locally
   *      (typed 401 ApiError, nothing sent).
   *   2. RETRY-ONCE: a 401 on a request made with auth INTENT (opts.auth !==
   *      false) triggers `onUnauthorized` ONCE; if it hands back a token, the
   *      request is replayed exactly once with that token. Any other outcome
   *      (no handler, auth:false, refresh yields null) returns the original
   *      401 response for `parse`/`requestBlob` to turn into an ApiError.
   */
  private async sendWithRetry(method: HttpMethod, path: string, opts: RequestOptions): Promise<Response> {
    const useAuth = opts.auth !== false;

    let gateToken: string | undefined;
    if (useAuth && this.onUnauthorized && this.getToken() === null) {
      const token = await this.onUnauthorized();
      if (token === null) throw new ApiError(401, 'unauthorized', 'Not signed in');
      gateToken = token;
    }

    const res = await this.send(method, path, opts, gateToken);
    if (res.status !== 401 || !useAuth || !this.onUnauthorized) return res;

    const freshToken = await this.onUnauthorized();
    if (freshToken === null) return res;
    return this.send(method, path, opts, freshToken);
  }

  /** The one place url/auth/body/fetch live; both request paths call through it. */
  private async send(
    method: HttpMethod,
    path: string,
    opts: RequestOptions,
    tokenOverride?: string,
  ): Promise<Response> {
    const url = this.buildUrl(path, opts.query);
    const headers = new Headers();
    const useAuth = opts.auth !== false;
    if (useAuth) {
      const token = tokenOverride ?? this.getToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    let payload: string | undefined;
    if (opts.body !== undefined) {
      headers.set('Content-Type', 'application/json');
      payload = JSON.stringify(opts.body);
    }

    // Every request gets a default timeout; a caller-supplied signal (e.g. a
    // stale-response guard) is combined with it rather than overridden, so
    // either can abort the request.
    const timeoutSignal = AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
    const signal = opts.signal ? AbortSignal.any([opts.signal, timeoutSignal]) : timeoutSignal;

    try {
      return await this.fetchImpl(url, { method, headers, body: payload, signal, credentials: 'include' });
    } catch {
      // Transport failure (offline, DNS, server down) or an abort (caller
      // cancel, or this request outliving DEFAULT_TIMEOUT_MS) -- either way
      // the caller never got a response, so both map to the same network
      // ApiError grammar; the message hints at a timeout when that was the
      // actual cause.
      const message = timeoutSignal.aborted ? 'Request timed out' : 'Cannot reach the server';
      throw new ApiError(0, 'network_error', message);
    }
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const body: unknown = text.length > 0 ? safeJson(text) : undefined;
    if (!res.ok) throw apiErrorFromBody(res.status, body);
    return body as T;
  }

  private buildUrl(path: string, query?: RequestOptions['query']): string {
    const base = `${this.baseUrl}${path}`;
    if (!query) return base;
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) sp.set(k, String(v));
    }
    const qs = sp.toString();
    return qs ? `${base}?${qs}` : base;
  }

  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, opts);
  }
  post<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, opts);
  }
  put<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, opts);
  }
  patch<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, opts);
  }
  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }
}

/**
 * Pull the filename from a `Content-Disposition: attachment; filename="..."`
 * header. A malformed percent-encoded filename (decodeURIComponent throws on
 * a lone/invalid `%` sequence) degrades to null rather than throwing, so
 * requestBlob's only ever-thrown type stays ApiError.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  const captured = match?.[1];
  if (!captured) return null;
  try {
    return decodeURIComponent(captured.trim());
  } catch {
    return null;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}
